import React, { useState, useEffect } from 'react';
import { motion } from "motion/react";
import { useEvents } from '@/features/events/hooks/useEvents';
import { useNavigate } from 'react-router-dom';
import { useLoadingContext } from '@/hooks';
import { useAuth } from '@/hooks/useAuth';
import { getBestImageUrl, getBestVideoUrl } from '@/lib/utils';
import { ImagePlaceholder, getEventPrice } from '@/features/home/utils';
import { likeService } from '@/features/likes/services/likeService';
import LikeButton from '@/components/LikeButton';
import type { Event } from '@/features/events/types/types';

const PopularEvents: React.FC = () => {
    const { events, loading, error } = useEvents();
    const navigate = useNavigate();
    const { startLoading, stopLoading } = useLoadingContext();
    const { isAuthenticated } = useAuth();
    const [hoveredEventId, setHoveredEventId] = useState<string | null>(null);
    const [shouldPlayVideo, setShouldPlayVideo] = useState<string | null>(null);
    const [likedMap, setLikedMap] = useState<Record<string, boolean>>({});

    // Trigger loading when data is loading
    useEffect(() => {
        if (loading) {
            startLoading('events-data');
        } else {
            stopLoading('events-data');
        }
    }, [loading, startLoading, stopLoading]);

    useEffect(() => {
        if (hoveredEventId) {
            const timer = setTimeout(() => {
                setShouldPlayVideo(hoveredEventId);
            }, 300); // 300ms delay
            return () => clearTimeout(timer);
        } else {
            setShouldPlayVideo(null);
        }
    }, [hoveredEventId]);

    // Fetch like status for events on initial load when user is authenticated
    // Note: Intentionally not re-fetching when isAuthenticated changes mid-session
    // to avoid race conditions with toggle operations
    useEffect(() => {
        const fetchLikeStatus = async () => {
            if (!isAuthenticated || events.length === 0) return;
            
            const eventIds = events.slice(0, 4).map(event => event.id);
            const response = await likeService.checkLikes({
                entity_ids: eventIds,
                entity_type: 'events',
            });
            setLikedMap(response.liked_map);
        };

        fetchLikeStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events]);

    const handleLikeToggle = (eventId: string, newLikedState: boolean) => {
        setLikedMap(prev => ({ ...prev, [eventId]: newLikedState }));
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2,
            },
        },
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
            },
        },
        hover: {
            scale: 1.02,
            transition: {
                duration: 0.2,
            },
        },
    };

    const handleEventClick = (event: Event) => {
        // Navigate to event details page
        navigate(`/venues/${event.business.handle}/events/${event.id}`);
    };

    if (loading) {
        return (
            <section id="events" className="max-w-8xl mx-auto px-8 py-16">
                <div className="text-center">
                    {/* Loading is handled by the global loader */}
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section id="events" className="max-w-8xl mx-auto px-8 py-16">
                <div className="text-center">
                    <p className="text-red-600 mb-4">{error}</p>
                    <p className="text-gray-400">Unable to load events at the moment</p>
                </div>
            </section>
        );
    }

    // Show only first 4 events for better UX
    const displayedEvents = events.slice(0, 4);

    return (
        <section id="events" className="max-w-8xl mx-auto px-8 py-16">
            <motion.div 
                className="text-center mb-16"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
            >
                <h2 className="text-5xl font-serif font-medium text-white">Popular Events</h2>
                <p className="text-lg text-gray-400 mt-4 max-w-2xl mx-auto">Don't miss out on the most sought-after events happening this week.</p>
            </motion.div>
            
            {displayedEvents.length > 0 ? (
                <>
                    <motion.div 
                        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 mb-16"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {displayedEvents.map((event) => {
                            const videoUrl = getBestVideoUrl(event.card_video_id);
                            const shouldPlay = shouldPlayVideo === event.id;
                            
                            return (
                                <motion.div 
                                    key={event.id} 
                                    className="group text-white cursor-pointer"
                                    variants={cardVariants}
                                    whileHover="hover"
                                    onClick={() => handleEventClick(event)}
                                    onMouseEnter={() => setHoveredEventId(event.id)}
                                    onMouseLeave={() => setHoveredEventId(null)}
                                >
                                    <div className="relative overflow-hidden bg-gray-900 rounded-3xl">
                                        <div className="aspect-[3/4]">
                                           {shouldPlay && videoUrl ? (
                                             <video
                                               src={videoUrl}
                                               autoPlay
                                               muted
                                               loop
                                               className="w-full h-full object-cover"
                                               onError={(e) => {
                                                 // Fallback to image if video fails to load
                                                 const target = e.target as HTMLVideoElement;
                                                 target.style.display = 'none';
                                                 const img = target.nextElementSibling as HTMLImageElement;
                                                 if (img) img.style.display = 'block';
                                               }}
                                             />
                                           ) : null}
                                           {getBestImageUrl(event.card_image_id, event.media, '') !== '' ? (
                                             <img
                                               src={getBestImageUrl(event.card_image_id, event.media, '/placeholder-event.jpg')}
                                               alt={event.name}
                                               className={`w-full h-full object-cover ${shouldPlay && videoUrl ? 'hidden' : 'block'}`}
                                               onError={(e) => {
                                                 // Fallback to placeholder if image fails to load
                                                 const target = e.target as HTMLImageElement;
                                                 target.style.display = 'none';
                                                 target.nextElementSibling?.classList.remove('hidden');
                                               }}
                                             />
                                           ) : null}
                                           <ImagePlaceholder 
                                             label={`${event.name} Flyer`} 
                                             className={getBestImageUrl(event.card_image_id, event.media, '') !== '' ? 'hidden' : ''} 
                                           />
                                        </div>
                                        
                                        {/* Gradient overlay for better text readability */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                                        
                                        {/* Like Button - Top Right */}
                                        <LikeButton
                                            entityId={event.id}
                                            entityType="events"
                                            isLiked={likedMap[event.id] || false}
                                            onToggle={(newState) => handleLikeToggle(event.id, newState)}
                                            className="absolute top-4 right-4 z-10"
                                        />
                                        
                                        {/* Event details overlay */}
                                        <div className="absolute bottom-0 left-0 right-0 p-4 transition-transform duration-300 group-hover:-translate-y-16">
                                            <h3 className="font-medium text-lg mb-1 text-white drop-shadow-lg">{event.name}</h3>
                                            <p className="text-sm text-gray-200 mb-2 drop-shadow-md">{event.business?.name || 'Unknown Venue'}</p>
                                            <div className="flex items-center justify-between">
                                                <span className="text-lg font-serif font-medium text-white drop-shadow-lg">
                                                    {getEventPrice(event) > 0 ? `$${getEventPrice(event)}` : 'Free'}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        {/* Hover overlay and Book Now button */}
                                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-t from-black/60 via-transparent"></div>
                                        <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-y-0 translate-y-2">
                                            <button className="w-full bg-white/95 backdrop-blur-sm text-black py-3 px-4 rounded-full font-medium hover:bg-white transition-colors">
                                                Book Now
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                    
                    <motion.div 
                        className="text-center"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                    >
                        <button
                            onClick={() => navigate('/events')}
                            className="inline-flex items-center px-8 py-4 bg-white/95 backdrop-blur-sm text-black font-medium rounded-full hover:bg-white transition-colors duration-200 shadow-lg hover:shadow-xl"
                        >
                            View All Events
                            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </button>
                    </motion.div>
                </>
            ) : (
                <div className="text-center">
                    <p className="text-gray-400">No events available at the moment</p>
                </div>
            )}
        </section>
    );
};

export default PopularEvents;
